import React, { createContext, useContext, useState } from "react";
import { Activity } from "./Activity";

type PrestudyContextType = {
    activities: Activity[],
    participants: string[],
}

const defaultState: PrestudyContextType = {
    activities: [],
    participants: [] as string[],
};

/**
 * Context for the prestudy session
 */
const PrestudyContext = createContext<PrestudyContextType>(defaultState);
const PrestudyUpdateContext = createContext<PrestudyUpdateContextType>({
    addActivity: (activity: Activity) => { },
    removeActivity: (activityNumber: number) => { },
    addParticipant: (participant: string) => { },
    removeParticipant: (participant: string) => { },
});

export function usePrestudyContext() {
    const prestudyContext = useContext(PrestudyContext);
    const updateContext = useContext(PrestudyUpdateContext);
    return {
        activityContext: {
            activities: prestudyContext.activities,
            addActivity: updateContext.addActivity,
            removeActivity: updateContext.removeActivity
        },
        participantContext: {
            participants: prestudyContext.participants,
            addParticipant: updateContext.addParticipant,
            removeParticipant: updateContext.removeParticipant
        }
    }
}

interface PrestudyUpdateContextType {
    addActivity: (activity: Activity) => void,
    removeActivity: (activityNumber: number) => void,
    addParticipant: (participant: string) => void,
    removeParticipant: (participant: string) => void,
}


export default function PrestudyContextProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState(defaultState);

    const addActivity = (activity: Activity) => {
        const newSortedState = ({
            ...state,
            activities: [...state.activities, activity]
        }).activities.sort((a, b) => a.duration - b.duration);

        setState({
            ...state,
            activities: newSortedState,
        });
    };

    const removeActivity = (activityNumber: number) => {
        setState({
            ...state,
            activities: state.activities.filter(act => act.number !== activityNumber)
        });
    };

    const addParticipant = (participant: string) => {
        setState({
            ...state,
            participants: [...state.participants, participant]
        });
    };

    const removeParticipant = (participant: string) => {
        setState({
            ...state,
            participants: state.participants.filter(p => p !== participant)
        });
    };

    const value = {
        addActivity,
        removeActivity,
        addParticipant,
        removeParticipant,
    };


    return (
        <PrestudyContext.Provider value={state}>
            <PrestudyUpdateContext.Provider value={value}>
                {children}
            </PrestudyUpdateContext.Provider>
        </PrestudyContext.Provider>
    );
};